import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { API_ORIGIN } from '../../services/apiConfig';
import {
  certificatePathValue,
  certificateSchema,
  formatCertificateGender,
  formatCertificateValue,
  maskCertificateValue,
  parseCertificateObject,
  renderCertificateText,
  toMarathiDigits,
} from '../../utils/certificateTemplate';
import './GovernmentCertificateCard.css';

export { formatCertificateGender, maskCertificateValue, toMarathiDigits };

const publicAssetUrl = value => {
  if (!value || /^https?:\/\//i.test(value) || String(value).startsWith('data:') || String(value).startsWith('/certificate-assets/')) return value || '';
  return `${API_ORIGIN}${String(value).startsWith('/') ? '' : '/'}${value}`;
};

const InlineEditable = ({ value, children, onChange, onActivate, multiline = false, label }) => {
  const [active, setActive] = useState(false);
  if (!onChange) return children || null;
  if (active) {
    const shared = {
      className: 'gov-cert-inline-input',
      value: value || '',
      onChange: event => onChange(event.target.value),
      onBlur: () => setActive(false),
      onKeyDown: event => {
        if (event.key === 'Escape' || (!multiline && event.key === 'Enter')) {
          event.preventDefault();
          event.currentTarget.blur();
        }
      },
      autoFocus: true,
      'aria-label': label || 'Edit certificate text',
    };
    return multiline ? <textarea {...shared} rows={3} /> : <input {...shared} />;
  }
  return (
    <span
      className="gov-cert-inline-editable"
      role="button"
      tabIndex={0}
      title="Click to edit this text"
      onClick={() => { onActivate?.(); setActive(true); }}
      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onActivate?.(); setActive(true); } }}
    >
      {children || <span className="gov-cert-inline-empty">Click to add text</span>}
    </span>
  );
};

const BilingualLine = ({
  marathi, english, className = '', marathiValue, englishValue, onMarathiChange, onEnglishChange,
  onMarathiActivate, onEnglishActivate, multiline = false,
}) => (
  <div className={className}>
    {(marathi || onMarathiChange) && <div className="gov-cert-marathi"><InlineEditable value={marathiValue} onChange={onMarathiChange} onActivate={onMarathiActivate} multiline={multiline} label="Edit Marathi certificate text">{marathi}</InlineEditable></div>}
    {(english || onEnglishChange) && <div className="gov-cert-english" lang="en" data-marathi-digits="false"><InlineEditable value={englishValue} onChange={onEnglishChange} onActivate={onEnglishActivate} multiline={multiline} label="Edit English certificate text">{english}</InlineEditable></div>}
  </div>
);

const TOKEN_PATTERN = /{{\s*([a-z][a-z0-9_.]*)(?:\|([a-z_]+))?\s*}}/gi;

const RichCertificateText = ({ text, context }) => {
  const source = String(text || '');
  const parts = [];
  let cursor = 0;
  TOKEN_PATTERN.lastIndex = 0;
  let match = TOKEN_PATTERN.exec(source);
  while (match) {
    if (match.index > cursor) parts.push(source.slice(cursor, match.index));
    const value = formatCertificateValue(certificatePathValue(context, match[1]), match[2] || 'text') || '—';
    parts.push(<strong className="gov-cert-variable" key={`${match.index}-${match[1]}`}>{value}</strong>);
    cursor = TOKEN_PATTERN.lastIndex;
    match = TOKEN_PATTERN.exec(source);
  }
  if (cursor < source.length) parts.push(source.slice(cursor));
  return parts.length ? parts : renderCertificateText(source, context);
};

const CertificatePhoto = ({ source, alt, placeholder, className, fallbackSource, fallbackAlt }) => {
  const [failedStage, setFailedStage] = useState(0);

  useEffect(() => {
    setFailedStage(0);
  }, [source, fallbackSource]);

  const activeSource = failedStage === 0 ? source : failedStage === 1 ? fallbackSource : '';
  if (!activeSource) {
    return <span className="gov-cert-photo-placeholder">{placeholder}</span>;
  }
  return (
    <img
      className={className}
      src={publicAssetUrl(activeSource)}
      alt={failedStage === 1 ? (fallbackAlt || alt) : alt}
      onError={() => setFailedStage(stage => stage + 1)}
      style={className === 'gov-cert-emblem' ? { width: 58, height: 80, maxWidth: 58, maxHeight: 80, objectFit: 'contain' } : undefined}
    />
  );
};

export const certificatePhotoSource = (block, context) => {
  const sourceType = block?.source_type || 'field';
  if (sourceType === 'upload') return block.image_url;
  if (sourceType === 'tenant_logo') return context?.tenant?.logo_url;
  if (sourceType === 'ashoka') return '/certificate-assets/ashoka-emblem.png';
  return certificatePathValue(context, block?.photo_path);
};

const PartyPanel = ({ block, context, editor, pathPrefix }) => {
  const renderSide = (side, sideClass) => {
    const item = block?.[side] || {};
    const name = formatCertificateValue(certificatePathValue(context, item.name_path), item.name_format || 'text') || '—';
    const photo = certificatePathValue(context, item.photo_path);
    const labelMr = item.label_mr || (side === 'left' ? 'वर' : 'वधू');
    const labelEn = item.label_en || (side === 'left' ? 'Husband' : 'Wife');
    return (
      <div className={`gov-cert-party gov-cert-party-${sideClass}`}>
        <div className="gov-cert-party-photo">
          <CertificatePhoto source={photo} alt={`${labelEn} photograph`} placeholder={`${labelMr}\n${labelEn}`} />
        </div>
        <div className="gov-cert-party-copy">
          <div className="gov-cert-party-label">
            <InlineEditable value={item.label_mr} onChange={editor ? value => editor.update(`${pathPrefix}.${side}.label_mr`, value) : null} onActivate={editor ? () => editor.activate(`${pathPrefix}.${side}.label_mr`) : null}>{labelMr}</InlineEditable>
            {' / '}
            <span><InlineEditable value={item.label_en} onChange={editor ? value => editor.update(`${pathPrefix}.${side}.label_en`, value) : null} onActivate={editor ? () => editor.activate(`${pathPrefix}.${side}.label_en`) : null}>{labelEn}</InlineEditable></span>:
          </div>
          <div className="gov-cert-party-name">{name}</div>
        </div>
      </div>
    );
  };

  return (
    <section className="gov-cert-party-panel">
      {renderSide('left', 'left')}
      {renderSide('right', 'right')}
    </section>
  );
};

const SinglePhotoBlock = ({ block, context, editor, pathPrefix }) => {
  const source = certificatePhotoSource(block, context);
  if (!editor && block.optional && !source) return null;
  const captionMr = renderCertificateText(block.caption_mr, context, '');
  const captionEn = renderCertificateText(block.caption_en, context, '');
  const label = captionEn || captionMr || 'Certificate photograph';
  const alignment = ['left', 'center', 'right'].includes(block.alignment) ? block.alignment : 'center';
  const size = ['small', 'medium', 'large'].includes(block.size) ? block.size : 'medium';
  const fit = block.fit === 'cover' ? 'cover' : 'contain';
  const hasCaption = Boolean(captionMr || captionEn || editor);
  return (
    <section className={`gov-cert-single-photo gov-cert-single-photo-${alignment} ${hasCaption ? 'gov-cert-single-photo-has-caption' : ''}`}>
      <div className={`gov-cert-single-photo-frame gov-cert-single-photo-${size} ${!block.show_border ? 'no-border' : ''} gov-cert-photo-fit-${fit}`}>
        <CertificatePhoto source={source} alt={label} placeholder={captionMr || captionEn || 'Photo'} />
      </div>
      {hasCaption && (
        <div className="gov-cert-single-photo-caption">
          <InlineEditable value={block.caption_mr} onChange={editor ? value => editor.update(`${pathPrefix}.caption_mr`, value) : null} onActivate={editor ? () => editor.activate(`${pathPrefix}.caption_mr`) : null}>{captionMr}</InlineEditable>
          {(captionMr || editor) && (captionEn || editor) && ' / '}
          <span><InlineEditable value={block.caption_en} onChange={editor ? value => editor.update(`${pathPrefix}.caption_en`, value) : null} onActivate={editor ? () => editor.activate(`${pathPrefix}.caption_en`) : null}>{captionEn}</InlineEditable></span>
        </div>
      )}
    </section>
  );
};

const ParagraphBlock = ({ block, context, editor, pathPrefix }) => (
  <section className={`gov-cert-paragraph gov-cert-tone-${block.tone || block.type || 'default'}`}>
    <BilingualLine
      marathi={<RichCertificateText text={block.text_mr} context={context} />}
      english={<RichCertificateText text={block.text_en} context={context} />}
      marathiValue={block.text_mr}
      englishValue={block.text_en}
      onMarathiChange={editor ? value => editor.update(`${pathPrefix}.text_mr`, value) : null}
      onEnglishChange={editor ? value => editor.update(`${pathPrefix}.text_en`, value) : null}
      onMarathiActivate={editor ? () => editor.activate(`${pathPrefix}.text_mr`) : null}
      onEnglishActivate={editor ? () => editor.activate(`${pathPrefix}.text_en`) : null}
      multiline
    />
  </section>
);

const FieldGridBlock = ({ block, context, editor, pathPrefix }) => {
  const items = Array.isArray(block.items) ? block.items.filter(item => {
    const value = certificatePathValue(context, item.path);
    return !item.optional || (value !== undefined && value !== null && String(value).trim() !== '');
  }) : [];

  return (
    <section className={`gov-cert-grid gov-cert-grid-${Math.max(1, Math.min(2, Number(block.columns) || 2))}`}>
      {items.map((item, index) => {
        const rawValue = formatCertificateValue(certificatePathValue(context, item.path), item.format || 'text');
        const value = (item.masked ? maskCertificateValue(rawValue) : rawValue) || '—';
        return (
          <div className="gov-cert-field" key={`${block.id || 'block'}-${item.path}-${index}`}>
            <div className="gov-cert-field-label">
              <InlineEditable value={item.label_mr} onChange={editor ? next => editor.update(`${pathPrefix}.items.${index}.label_mr`, next) : null} onActivate={editor ? () => editor.activate(`${pathPrefix}.items.${index}.label_mr`) : null}>{item.label_mr}</InlineEditable>
              {item.label_mr && item.label_en && ' / '}
              <InlineEditable value={item.label_en} onChange={editor ? next => editor.update(`${pathPrefix}.items.${index}.label_en`, next) : null} onActivate={editor ? () => editor.activate(`${pathPrefix}.items.${index}.label_en`) : null}>{item.label_en}</InlineEditable>
            </div>
            <div className="gov-cert-field-value">{value}</div>
          </div>
        );
      })}
    </section>
  );
};

const LayoutAddBar = ({ region, editor, compact = false }) => {
  if (!editor || editor.builder) return null;
  const body = region === 'blocks';
  return (
    <div className={`gov-cert-layout-add ${compact ? 'compact' : ''}`}>
      <button type="button" onClick={() => editor.addBlock(region, 'paragraph')}>+ Text</button>
      <button type="button" onClick={() => editor.addBlock(region, 'notice')}>+ Notice</button>
      {body && <button type="button" onClick={() => editor.addBlock(region, 'field_grid')}>+ Fields</button>}
      {body && <button type="button" onClick={() => editor.addBlock(region, 'party_panel')}>+ Parties</button>}
      {body && <button type="button" onClick={() => editor.addBlock(region, 'photo')} disabled={editor.canAddPhoto === false} title={editor.canAddPhoto === false ? 'Configure a photo document or image field first' : undefined}>+ Photo</button>}
    </div>
  );
};

export const GRID_SIZE = 10;
export const SNAP_EDGE_THRESHOLD = 8;

export const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

export const minimumFreeBlockSize = block => {
  if (block?.type === 'party_panel') return { width: 280, height: 80 };
  if (block?.type === 'field_grid') return { width: 140, height: 35 };
  if (block?.type === 'photo') return { width: 60, height: 60 };
  return { width: 80, height: 30 };
};

export const snapToGrid = (value, gridSize = GRID_SIZE) => Math.round(value / gridSize) * gridSize;

export const snapPosition = (rawVal, dimension, maxBound, gridSize = GRID_SIZE, threshold = SNAP_EDGE_THRESHOLD) => {
  const maxPos = Math.max(0, maxBound - dimension);
  if (Math.abs(rawVal) <= threshold) return 0;
  if (Math.abs(rawVal - maxPos) <= threshold || Math.abs(rawVal + dimension - maxBound) <= threshold) {
    return maxPos;
  }
  const centerPos = (maxBound - dimension) / 2;
  if (Math.abs(rawVal - centerPos) <= threshold) {
    return Math.round(centerPos / gridSize) * gridSize;
  }
  const snapped = Math.round(rawVal / gridSize) * gridSize;
  return clamp(snapped, 0, maxPos);
};

export const snapDimension = (rawDim, currentPos, maxBound, minDim, gridSize = GRID_SIZE, threshold = SNAP_EDGE_THRESHOLD) => {
  const maxAvailable = Math.max(minDim, maxBound - currentPos);
  if (Math.abs(currentPos + rawDim - maxBound) <= threshold || Math.abs(rawDim - maxAvailable) <= threshold) {
    return maxAvailable;
  }
  const snapped = Math.round(rawDim / gridSize) * gridSize;
  return clamp(snapped, minDim, maxAvailable);
};

const DesignerBlockFrame = ({ block, index, region, editor, freeLayout, canvasHeight, children }) => {
  const interaction = useRef(null);
  const selected = editor?.selectedBlockId === block.id;
  const layout = block.layout || { x: 0, y: 0, width: 740, height: block.type === 'photo' ? 120 : 60, z_index: index };
  const beginInteraction = (event, mode) => {
    event.preventDefault();
    event.stopPropagation();
    editor?.selectBlock?.(block.id);
    interaction.current = {
      mode,
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      layout: { ...layout },
      unitX: event.currentTarget.closest('.gov-cert-free-canvas')?.getBoundingClientRect().width / 740 || 1,
      unitY: event.currentTarget.closest('.gov-cert-free-canvas')?.getBoundingClientRect().height / canvasHeight || 1,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const updateInteraction = event => {
    const active = interaction.current;
    if (!active || active.pointerId !== event.pointerId) return;
    const deltaX = (event.clientX - active.clientX) / active.unitX;
    const deltaY = (event.clientY - active.clientY) / active.unitY;
    const start = active.layout;
    if (active.mode === 'move') {
      const width = Number(start.width) || 740;
      const height = Number(start.height) || 60;
      const rawX = (Number(start.x) || 0) + deltaX;
      const rawY = (Number(start.y) || 0) + deltaY;
      const snappedX = snapPosition(rawX, width, 740);
      const snappedY = snapPosition(rawY, height, canvasHeight);
      editor.updateBlockLayout?.(region, index, {
        ...start,
        x: snappedX,
        y: snappedY,
      });
    } else {
      const x = Number(start.x) || 0;
      const y = Number(start.y) || 0;
      const minimum = minimumFreeBlockSize(block);
      const rawWidth = (Number(start.width) || 160) + deltaX;
      const rawHeight = (Number(start.height) || 60) + deltaY;
      let nextWidth = snapDimension(rawWidth, x, 740, minimum.width);
      let nextHeight = snapDimension(rawHeight, y, canvasHeight, minimum.height);
      const isPhoto = block.type === 'photo' || block.source_type === 'ashoka' || block.source_type === 'tenant_logo';
      if (isPhoto && Number(start.width) > 0 && Number(start.height) > 0) {
        const ratio = Number(start.width) / Number(start.height);
        if (Math.abs(deltaX) >= Math.abs(deltaY)) {
          nextHeight = snapDimension(nextWidth / ratio, y, canvasHeight, minimum.height);
        } else {
          nextWidth = snapDimension(nextHeight * ratio, x, 740, minimum.width);
        }
      }
      editor.updateBlockLayout?.(region, index, {
        ...start,
        width: nextWidth,
        height: nextHeight,
      });
    }
  };
  const endInteraction = event => {
    if (interaction.current?.pointerId === event.pointerId) interaction.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };
  const moveWithKeyboard = event => {
    const direction = {
      ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
    }[event.key];
    if (!direction) return;
    event.preventDefault();
    event.stopPropagation();
    editor?.selectBlock?.(block.id);
    const step = event.shiftKey ? 10 : 1;
    const width = Number(layout.width) || 740;
    const height = Number(layout.height) || 60;
    const targetX = Math.round(clamp((Number(layout.x) || 0) + (direction[0] * step), 0, Math.max(0, 740 - width)));
    const targetY = Math.round(clamp((Number(layout.y) || 0) + (direction[1] * step), 0, Math.max(0, canvasHeight - height)));
    editor?.updateBlockLayout?.(region, index, {
      ...layout,
      x: targetX,
      y: targetY,
    });
  };
  const resizeWithKeyboard = event => {
    const direction = {
      ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
    }[event.key];
    if (!direction) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 10 : 1;
    const x = Number(layout.x) || 0;
    const y = Number(layout.y) || 0;
    const minimum = minimumFreeBlockSize(block);
    const targetWidth = Math.round(clamp((Number(layout.width) || 160) + (direction[0] * step), minimum.width, 740 - x));
    const targetHeight = Math.round(clamp((Number(layout.height) || 60) + (direction[1] * step), minimum.height, canvasHeight - y));
    editor?.updateBlockLayout?.(region, index, {
      ...layout,
      width: targetWidth,
      height: targetHeight,
    });
  };
  const style = freeLayout ? {
    left: `${((Number(layout.x) || 0) / 740) * 100}%`,
    top: `${((Number(layout.y) || 0) / canvasHeight) * 100}%`,
    width: `${((Number(layout.width) || 740) / 740) * 100}%`,
    height: `${((Number(layout.height) || 60) / canvasHeight) * 100}%`,
    zIndex: Number.isFinite(Number(layout.z_index)) ? Number(layout.z_index) : index,
  } : undefined;
  return (
    <div
      className={`${editor ? 'gov-cert-designer-block' : ''} ${freeLayout ? 'gov-cert-free-block' : ''} ${selected ? 'selected' : ''}`}
      style={style}
      role={editor && freeLayout ? 'group' : undefined}
      aria-label={editor && freeLayout ? `${selected ? 'Selected ' : ''}${block.type || 'content'} certificate block` : undefined}
      onClick={event => { if (editor) { event.stopPropagation(); editor.selectBlock?.(block.id); } }}
    >
      {editor && !freeLayout && (
        <div className="gov-cert-block-tools">
          <span>{region === 'blocks' ? 'Body' : region.startsWith('header') ? 'Header' : 'Footer'} block</span>
          <button type="button" onClick={() => editor.moveBlock(region, index, index - 1)} disabled={!index} aria-label="Move block up">↑</button>
          <button type="button" onClick={() => editor.moveBlock(region, index, index + 1)} disabled={index === editor.blockCount - 1} aria-label="Move block down">↓</button>
          <button type="button" onClick={() => editor.removeBlock(region, index)} aria-label="Remove block">×</button>
        </div>
      )}
      {editor && freeLayout && (
        <button
          type="button"
          className="gov-cert-canvas-move"
          aria-label="Move certificate block. Use arrow keys to reposition"
          aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
          aria-pressed={selected}
          title="Drag to move, or use the arrow keys"
          onFocus={() => editor?.selectBlock?.(block.id)}
          onKeyDown={moveWithKeyboard}
          onPointerDown={event => beginInteraction(event, 'move')}
          onPointerMove={updateInteraction}
          onPointerUp={endInteraction}
          onPointerCancel={endInteraction}
        >⠿</button>
      )}
      {freeLayout ? <div className="gov-cert-free-block-content">{children}</div> : children}
      {editor && freeLayout && selected && (
        <button
          type="button"
          className="gov-cert-canvas-delete"
          aria-label="Delete component"
          title="Delete component"
          onClick={event => {
            event.stopPropagation();
            editor.removeBlock?.(region, index);
          }}
        >✕</button>
      )}
      {editor && freeLayout && selected && (
        <button
          type="button"
          className="gov-cert-canvas-resize"
          aria-label="Resize certificate block. Use arrow keys to resize"
          aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
          title="Drag to resize, or use the arrow keys"
          onKeyDown={resizeWithKeyboard}
          onPointerDown={event => beginInteraction(event, 'resize')}
          onPointerMove={updateInteraction}
          onPointerUp={endInteraction}
          onPointerCancel={endInteraction}
        />
      )}
    </div>
  );
};

const CertificateBlocks = ({ blocks, context, editor, region, freeLayout = false, canvasHeight = 520 }) => (
  <>
    {(Array.isArray(blocks) ? blocks : []).map((block, index) => {
      const pathPrefix = `${region}.${index}`;
      return (
        <DesignerBlockFrame block={block} index={index} region={region} editor={editor ? { ...editor, blockCount: blocks.length } : null} freeLayout={freeLayout} canvasHeight={canvasHeight} key={block.id || index}>
          {block.type === 'party_panel'
            ? <PartyPanel block={block} context={context} editor={editor} pathPrefix={pathPrefix} />
            : block.type === 'photo'
              ? <SinglePhotoBlock block={block} context={context} editor={editor} pathPrefix={pathPrefix} />
            : block.type === 'field_grid'
              ? <FieldGridBlock block={block} context={context} editor={editor} pathPrefix={pathPrefix} />
              : <ParagraphBlock block={block} context={context} editor={editor} pathPrefix={pathPrefix} />}
        </DesignerBlockFrame>
      );
    })}
    <LayoutAddBar region={region} editor={editor} compact={region !== 'blocks'} />
  </>
);

const legacyMarriageBlocks = [
  {
    id: 'marriage-party-panel',
    type: 'party_panel',
    left: { label_mr: 'वर', label_en: 'Husband', name_path: 'fields.groom_name', photo_path: 'fields.groom_photo_url' },
    right: { label_mr: 'वधू', label_en: 'Wife', name_path: 'fields.bride_name', photo_path: 'fields.bride_photo_url' },
  },
  { id: 'marriage-intro', type: 'paragraph', text_mr: 'प्रमाणित करण्यात येते की,', text_en: 'Certified that the marriage between,' },
  {
    id: 'marriage-groom', type: 'paragraph',
    text_mr: 'पतीचे नाव :- {{fields.groom_name}} राहणार {{fields.groom_address}} आणि',
    text_en: 'Husband Name :- {{fields.groom_name_en|uppercase}} residing at {{fields.groom_address|uppercase}} and',
  },
  {
    id: 'marriage-bride', type: 'paragraph',
    text_mr: 'पत्नीचे नाव :- {{fields.bride_name}} राहणार {{fields.bride_address}}',
    text_en: 'Wife Name :- {{fields.bride_name_en|uppercase}} residing at {{fields.bride_address|uppercase}}.',
  },
  {
    id: 'marriage-event', type: 'paragraph',
    text_mr: 'यांचा विवाह दिनांक {{fields.marriage_date|date_mr}} रोजी {{fields.marriage_place}} येथे विवाह विधी संपन्न झाला.',
    text_en: 'The marriage was solemnized on {{fields.marriage_date|date_en}} at {{fields.marriage_place}}.',
  },
  {
    id: 'marriage-register', type: 'paragraph',
    text_mr: 'महाराष्ट्र विवाह मंडळाचे विनियमन आणि विवाह नोंदणी अधिनियम, १९९८ अन्वये ठेवण्यात आलेल्या नोंदवहीच्या खंड क्रमांक {{certificate.register_volume|marathi_digits}} मधील अनुक्रमांक {{certificate.register_serial|marathi_digits}} वर दिनांक {{certificate.registration_date|date_mr}} रोजी सदर विवाहाची नोंदणी करण्यात आली आहे.',
    text_en: 'The marriage is registered under the Maharashtra Regulation of Marriage Bureaus and Registration of Marriages Act, 1998, in Volume {{certificate.register_volume}}, Serial No. {{certificate.register_serial}}, on {{certificate.registration_date|date_en}}.',
  },
  {
    id: 'marriage-witnesses', type: 'paragraph', tone: 'compact',
    text_mr: 'साक्षीदार: (१) {{fields.witness_1}}  (२) {{fields.witness_2}}',
    text_en: 'Witnesses: (1) {{fields.witness_1}}  (2) {{fields.witness_2}}',
  },
];

export default function GovernmentCertificateCard({ templateSnapshot, dataSnapshot, issuance, editor = null }) {
  const [dragOverRegion, setDragOverRegion] = useState(null);
  const template = parseCertificateObject(templateSnapshot);
  const sourceSchema = template?.schema || template;
  const schema = certificateSchema(template);
  const context = parseCertificateObject(dataSnapshot);
  if (!schema?.header || !context?.certificate) return null;

  const accent = schema.page?.accent_color || '#1a237e';
  const body = schema.body || {};
  const signature = schema.signature || {};
  const footer = schema.footer || {};
  const edit = (path, value) => editor?.update(path, value);
  const freeLayout = body.layout_mode === 'free';
  const canvasHeight = Math.max(300, Math.min(560, Number(body.canvas_height) || 520));
  const blocks = Array.isArray(schema.blocks) ? schema.blocks : [];
  const isMarriage = context.certificate.type === 'marriage';
  const isLegacyMarriage = isMarriage
    && !editor
    && sourceSchema?.body?.presentation_mode !== 'builder'
    && sourceSchema?.body?.layout_mode !== 'free'
    && !blocks.some(block => ['party_panel', 'photo'].includes(block.type));
  const sourceHeader = schema.header || {};
  const header = isLegacyMarriage ? {
    ...sourceHeader,
    authority_reference_mr: sourceHeader.authority_reference_mr || 'भाग चार-ब] महाराष्ट्र शासन राजपत्र, मे २०, १९९९ / वैशाख ३०, शके १९२१',
    form_label_mr: 'नमुना - "इ"',
    form_label_en: 'FORM "E"',
    legal_reference_mr: '(पहा कलम ६ (१) आणि नियम ५)',
    legal_reference_en: '(See Section 6 (1) and Rule 5)',
  } : sourceHeader;
  const displayBlocks = isLegacyMarriage
    ? legacyMarriageBlocks
    : blocks;
  const headerBlocks = Array.isArray(header.content_blocks) ? header.content_blocks : [];
  const dynamicHeaderHeight = headerBlocks.reduce((max, block) => {
    const y = Number(block.layout?.y) || 0;
    const h = Number(block.layout?.height) || 60;
    return Math.max(max, y + h);
  }, 0);
  const headerCanvasHeight = dynamicHeaderHeight > 0 ? Math.max(80, dynamicHeaderHeight + 10) : 0;

  const footerBlocks = Array.isArray(footer.content_blocks) ? footer.content_blocks : [];
  const dynamicFooterHeight = footerBlocks.reduce((max, block) => {
    const y = Number(block.layout?.y) || 0;
    const h = Number(block.layout?.height) || 60;
    return Math.max(max, y + h);
  }, 0);
  const footerCanvasHeight = dynamicFooterHeight > 0 ? Math.max(60, dynamicFooterHeight + 10) : 0;
  const certificateNumber = context.certificate.number || issuance?.certificate_number || '—';
  const rawVerificationValue = context.certificate.verification_url || context.certificate.verification_hash || certificateNumber;
  const verificationValue = String(rawVerificationValue).startsWith('/')
    ? `${window.location.origin}${rawVerificationValue}`
    : rawVerificationValue;
  const location = [context.tenant?.taluka, context.tenant?.district, context.tenant?.state, context.tenant?.pincode].filter(Boolean).join(', ');
  const emblemUrl = header.emblem_source === 'tenant_logo'
    ? context.tenant?.logo_url
    : header.emblem_source === 'custom'
      ? header.emblem_url
      : '/certificate-assets/ashoka-emblem.png';
  const layoutStyle = {
    '--certificate-font-family': schema.page?.font_family ? `'${schema.page.font_family}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif` : undefined,
    '--certificate-heading-font': schema.page?.heading_font_family ? `'${schema.page.heading_font_family}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif` : undefined,
    '--certificate-font-scale': Number(schema.page?.font_scale) || 1,
    '--certificate-accent': accent,
    '--certificate-page-background': schema.page?.background_color || '#ffffff',
    '--certificate-page-text': schema.page?.text_color || '#111827',
    '--certificate-border': schema.page?.border_color || accent,
    '--certificate-border-width': `${Number(schema.page?.border_width ?? 2)}px`,
    '--certificate-horizontal-padding': `${Number(schema.page?.horizontal_padding ?? 40)}px`,
    '--certificate-header-background': header.background_color || '#ffffff',
    '--certificate-header-text': header.text_color || '#0f172a',
    '--certificate-title': header.title_color || accent,
    '--certificate-body-background': body.background_color || '#ffffff',
    '--certificate-body-text': body.text_color || '#172033',
    '--certificate-body-secondary': body.secondary_text_color || '#64748b',
    '--certificate-variable': body.variable_color || accent,
    '--certificate-field-background': body.field_background_color || '#ffffff',
    '--certificate-field-border': body.field_border_color || '#d7dee8',
    '--certificate-field-border-width': body.show_field_borders === true ? '1px' : '0px',
    '--certificate-block-gap': `${Number(body.block_gap ?? 9)}px`,
    '--certificate-footer-background': footer.background_color || '#ffffff',
    '--certificate-footer-text': footer.text_color || '#64748b',
    '--certificate-footer-accent': footer.accent_color || accent,
  };

  const handleRegionDragOver = (event, regionName) => {
    if (editor) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      if (dragOverRegion !== regionName) setDragOverRegion(regionName);
    }
  };

  const handleRegionDragLeave = (event, regionName) => {
    if (editor && dragOverRegion === regionName) {
      event.stopPropagation();
      setDragOverRegion(null);
    }
  };

  const handleRegionDrop = (event, targetRegion) => {
    if (!editor) return;
    event.preventDefault();
    event.stopPropagation();
    setDragOverRegion(null);
    const type = event.dataTransfer.getData('application/x-certificate-block') || 'paragraph';
    const variable = event.dataTransfer.getData('application/x-certificate-variable');

    const regionCanvasHeight = targetRegion.startsWith('header') ? 120 : targetRegion.startsWith('footer') ? 140 : canvasHeight;
    const bounds = event.currentTarget.getBoundingClientRect();
    const minSize = minimumFreeBlockSize({ type });
    const rawX = ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * 740;
    const rawY = ((event.clientY - bounds.top) / Math.max(bounds.height, 1)) * regionCanvasHeight;
    const placement = {
      x: snapPosition(rawX, minSize.width, 740),
      y: snapPosition(rawY, minSize.height, regionCanvasHeight),
    };

    if (variable) {
      if (targetRegion === 'blocks') {
        editor.dropBlock?.('field', { placement, variable: { path: variable } }, 'blocks');
      } else {
        editor.dropBlock?.('paragraph', { placement, text_mr: `{{${variable}}}`, text_en: `{{${variable}}}` }, targetRegion);
      }
    } else {
      editor.dropBlock?.(type, placement, targetRegion);
    }
  };

  return (
    <article className={`gov-cert-frame ${editor ? 'gov-cert-design-mode' : ''}`} style={layoutStyle}>
      {header.visible !== false ? <header
        className={`gov-cert-header gov-cert-align-${header.alignment || 'center'} ${dragOverRegion === 'header' ? 'gov-cert-drag-over' : ''} ${header.layout_mode === 'free' ? 'gov-cert-header-free-mode' : ''}`}
        style={{
          position: 'relative',
          minHeight: headerCanvasHeight > 0 ? `${Math.max(120, headerCanvasHeight)}px` : undefined,
        }}
        onDragOver={e => handleRegionDragOver(e, 'header')}
        onDragLeave={e => handleRegionDragLeave(e, 'header')}
        onDrop={e => handleRegionDrop(e, 'header.content_blocks')}
      >
        {header.layout_mode === 'free' ? (
          <div
            className="gov-cert-free-canvas gov-cert-header-canvas"
            style={{ minHeight: `${Math.max(120, headerCanvasHeight)}px` }}
            onClick={e => { if (editor?.builder) { e.stopPropagation(); editor.selectBlock?.(null); } }}
          >
            {header.show_emblem !== false && !headerBlocks.some(b => b.type === 'photo') && (
              <div className="gov-cert-brand-row" style={{ pointerEvents: 'none' }}>
                <CertificatePhoto
                  className="gov-cert-emblem"
                  source={emblemUrl || '/certificate-assets/ashoka-emblem.png'}
                  fallbackSource="/certificate-assets/ashoka-emblem.png"
                  alt={header.emblem_source === 'tenant_logo' ? 'Gram Panchayat logo' : header.emblem_source === 'custom' ? 'Certificate header emblem' : 'भारताचे राजचिन्ह — सत्यमेव जयते'}
                  fallbackAlt="भारताचे राजचिन्ह — सत्यमेव जयते"
                  placeholder="Certificate emblem"
                />
              </div>
            )}
            <CertificateBlocks
              blocks={header.content_blocks}
              context={context}
              editor={editor}
              region="header.content_blocks"
              freeLayout
              canvasHeight={Math.max(120, headerCanvasHeight)}
            />
          </div>
        ) : (
          <>
            {editor && !editor.builder && <div className="gov-cert-region-label">HEADER · click any outlined text to edit</div>}
            {header.show_emblem !== false && <div className="gov-cert-brand-row">
              <CertificatePhoto
                className="gov-cert-emblem"
                source={emblemUrl || '/certificate-assets/ashoka-emblem.png'}
                fallbackSource="/certificate-assets/ashoka-emblem.png"
                alt={header.emblem_source === 'tenant_logo' ? 'Gram Panchayat logo' : header.emblem_source === 'custom' ? 'Certificate header emblem' : 'भारताचे राजचिन्ह — सत्यमेव जयते'}
                fallbackAlt="भारताचे राजचिन्ह — सत्यमेव जयते"
                placeholder="Certificate emblem"
              />
            </div>}
            {header.show_government_label !== false && <BilingualLine
              className="gov-cert-government"
              marathi={renderCertificateText(header.government_label_mr, context)}
              english={renderCertificateText(header.government_label_en, context)}
              marathiValue={header.government_label_mr}
              englishValue={header.government_label_en}
              onMarathiChange={editor ? value => edit('header.government_label_mr', value) : null}
              onEnglishChange={editor ? value => edit('header.government_label_en', value) : null}
              onMarathiActivate={editor ? () => editor.activate('header.government_label_mr') : null}
              onEnglishActivate={editor ? () => editor.activate('header.government_label_en') : null}
            />}
            {header.show_authority_reference !== false && (header.authority_reference_mr || header.authority_reference_en || isMarriage || editor) && (
              <div className="gov-cert-authority-reference">
                <InlineEditable value={header.authority_reference_mr} onChange={editor ? value => edit('header.authority_reference_mr', value) : null} onActivate={editor ? () => editor.activate('header.authority_reference_mr') : null}>
                  {renderCertificateText(header.authority_reference_mr || (isMarriage ? 'भाग चार-ब] महाराष्ट्र शासन राजपत्र, मे २०, १९९९ / वैशाख ३०, शके १९२१' : ''), context)}
                </InlineEditable>
                {(header.authority_reference_en || editor) && <span className="gov-cert-english" lang="en" data-marathi-digits="false"> / <InlineEditable value={header.authority_reference_en} onChange={editor ? value => edit('header.authority_reference_en', value) : null} onActivate={editor ? () => editor.activate('header.authority_reference_en') : null}>{renderCertificateText(header.authority_reference_en, context)}</InlineEditable></span>}
              </div>
            )}
            {header.show_form_label !== false && <div className="gov-cert-form-label">
              <InlineEditable value={header.form_label_mr} onChange={editor ? value => edit('header.form_label_mr', value) : null} onActivate={editor ? () => editor.activate('header.form_label_mr') : null}>{renderCertificateText(header.form_label_mr, context)}</InlineEditable>
              {header.form_label_mr && header.form_label_en && ' / '}
              <span className="gov-cert-english" lang="en" data-marathi-digits="false"><InlineEditable value={header.form_label_en} onChange={editor ? value => edit('header.form_label_en', value) : null} onActivate={editor ? () => editor.activate('header.form_label_en') : null}>{renderCertificateText(header.form_label_en, context)}</InlineEditable></span>
            </div>}
            {header.show_title !== false && <BilingualLine
              className="gov-cert-title"
              marathi={renderCertificateText(header.title_mr, context)}
              english={renderCertificateText(header.title_en, context)}
              marathiValue={header.title_mr}
              englishValue={header.title_en}
              onMarathiChange={editor ? value => edit('header.title_mr', value) : null}
              onEnglishChange={editor ? value => edit('header.title_en', value) : null}
              onMarathiActivate={editor ? () => editor.activate('header.title_mr') : null}
              onEnglishActivate={editor ? () => editor.activate('header.title_en') : null}
            />}
            {header.show_legal_reference !== false && <div className="gov-cert-legal">
              <InlineEditable value={header.legal_reference_mr} onChange={editor ? value => edit('header.legal_reference_mr', value) : null} onActivate={editor ? () => editor.activate('header.legal_reference_mr') : null}>{renderCertificateText(header.legal_reference_mr, context)}</InlineEditable>
              {header.legal_reference_mr && header.legal_reference_en && ' / '}
              <span className="gov-cert-english" lang="en" data-marathi-digits="false"><InlineEditable value={header.legal_reference_en} onChange={editor ? value => edit('header.legal_reference_en', value) : null} onActivate={editor ? () => editor.activate('header.legal_reference_en') : null}>{renderCertificateText(header.legal_reference_en, context)}</InlineEditable></span>
            </div>}
            {(headerBlocks.length > 0 || editor || dragOverRegion === 'header') && (
              <div
                className="gov-cert-header-canvas"
                onClick={e => { if (editor?.builder) { e.stopPropagation(); editor.selectBlock?.(null); } }}
              >
                <CertificateBlocks blocks={header.content_blocks} context={context} editor={editor} region="header.content_blocks" freeLayout={freeLayout || Boolean(editor)} canvasHeight={Math.max(180, headerCanvasHeight)} />
              </div>
            )}
          </>
        )}
      </header> : editor && <div
        className={`gov-cert-hidden-region ${dragOverRegion === 'header' ? 'gov-cert-drag-over' : ''}`}
        onDragOver={e => handleRegionDragOver(e, 'header')}
        onDragLeave={e => handleRegionDragLeave(e, 'header')}
        onDrop={e => handleRegionDrop(e, 'header.content_blocks')}
      >Header hidden — enable it from Header settings</div>}

      {header.visible !== false && header.show_certificate_number !== false && <div className="gov-cert-reference-row">
        <span><strong>प्रमाणपत्र क्र.:</strong> {toMarathiDigits(certificateNumber)}</span>
        <span lang="en" data-marathi-digits="false"><strong>Certificate No.:</strong> {certificateNumber}</span>
      </div>}

      <main
        className={`gov-cert-content gov-cert-align-${body.alignment || 'left'} ${dragOverRegion === 'body' ? 'gov-cert-drag-over' : ''}`}
        onDragOver={e => handleRegionDragOver(e, 'body')}
        onDragLeave={e => handleRegionDragLeave(e, 'body')}
        onDrop={e => handleRegionDrop(e, 'blocks')}
      >
        {editor && !editor.builder && <div className="gov-cert-region-label">BODY · edit, add, remove or reorder content here</div>}
        {freeLayout ? (
          <div
            className="gov-cert-free-canvas"
            style={{ aspectRatio: `740 / ${canvasHeight}` }}
            onClick={() => editor?.builder && editor.selectBlock?.(null)}
          >
            <CertificateBlocks blocks={displayBlocks} context={context} editor={isLegacyMarriage ? null : editor} region="blocks" freeLayout canvasHeight={canvasHeight} />
          </div>
        ) : <CertificateBlocks blocks={displayBlocks} context={context} editor={isLegacyMarriage ? null : editor} region="blocks" />}
        {editor && isLegacyMarriage && <div className="gov-cert-hidden-region">Save this draft once to convert the legacy body into editable blocks.</div>}
      </main>

      {footer.visible !== false ? <footer
        className={`gov-cert-footer-container ${dragOverRegion === 'footer' ? 'gov-cert-drag-over' : ''}`}
        onDragOver={e => handleRegionDragOver(e, 'footer')}
        onDragLeave={e => handleRegionDragLeave(e, 'footer')}
        onDrop={e => handleRegionDrop(e, 'footer.content_blocks')}
      >
        {editor && !editor.builder && <div className="gov-cert-region-label gov-cert-footer-region-label">FOOTER · click signature and verification text to edit</div>}
        {(footerBlocks.length > 0 || editor || dragOverRegion === 'footer') && (
          <div
            className="gov-cert-footer-canvas"
            style={{
              position: 'relative',
              minHeight: footerCanvasHeight > 0 ? `${footerCanvasHeight}px` : '0px',
            }}
            onClick={e => { if (editor?.builder) { e.stopPropagation(); editor.selectBlock?.(null); } }}
          >
            <CertificateBlocks blocks={footer.content_blocks} context={context} editor={editor} region="footer.content_blocks" freeLayout={freeLayout || Boolean(editor)} canvasHeight={Math.max(140, footerCanvasHeight)} />
          </div>
        )}
        <div className="gov-cert-footer">
        {footer.show_place_date !== false || footer.show_left_signature !== false ? <div className="gov-cert-signature-column">
          {footer.show_place_date !== false && <div className="gov-cert-place-date">
            <div><strong>ठिकाण / Place:</strong> {context.tenant?.name || '—'}</div>
            <div><strong>दिनांक / Date:</strong> {formatCertificateValue(context.certificate.registration_date, 'date')}</div>
          </div>}
          {footer.show_left_signature !== false && <><div className="gov-cert-signature-line" />
          <BilingualLine
            marathi={renderCertificateText(signature.left_label_mr, context)}
            english={renderCertificateText(signature.left_label_en, context)}
            marathiValue={signature.left_label_mr}
            englishValue={signature.left_label_en}
            onMarathiChange={editor ? value => edit('signature.left_label_mr', value) : null}
            onEnglishChange={editor ? value => edit('signature.left_label_en', value) : null}
            onMarathiActivate={editor ? () => editor.activate('signature.left_label_mr') : null}
            onEnglishActivate={editor ? () => editor.activate('signature.left_label_en') : null}
          /></>}
        </div> : <div />}

        {footer.show_qr !== false ? <div className="gov-cert-qr">
          <QRCodeSVG value={verificationValue} size={90} level="M" includeMargin />
          <code>{String(context.certificate.verification_hash || '').slice(0, 16).toUpperCase()}</code>
        </div> : <div />}

        {footer.show_right_signature !== false ? <div className="gov-cert-signature-column gov-cert-signature-right">
          <div className="gov-cert-signature-spacer" />
          <div className="gov-cert-signature-line" />
          <BilingualLine
            marathi={renderCertificateText(signature.right_label_mr, context)}
            english={renderCertificateText(signature.right_label_en, context)}
            marathiValue={signature.right_label_mr}
            englishValue={signature.right_label_en}
            onMarathiChange={editor ? value => edit('signature.right_label_mr', value) : null}
            onEnglishChange={editor ? value => edit('signature.right_label_en', value) : null}
            onMarathiActivate={editor ? () => editor.activate('signature.right_label_mr') : null}
            onEnglishActivate={editor ? () => editor.activate('signature.right_label_en') : null}
          />
          <strong>{context.tenant?.name}</strong>
          <small>{location}</small>
        </div> : <div />}
        </div>

        {editor && (!Array.isArray(footer.content_blocks) || footer.content_blocks.length === 0) && (
          <div className="gov-cert-footer-content"><LayoutAddBar region="footer.content_blocks" editor={editor} compact /></div>
        )}
        {footer.show_verification !== false && <div className="gov-cert-verification-copy">
          <BilingualLine
            marathi={renderCertificateText(footer.verification_text_mr, context)}
            english={renderCertificateText(footer.verification_text_en, context)}
            marathiValue={footer.verification_text_mr}
            englishValue={footer.verification_text_en}
            onMarathiChange={editor ? value => edit('footer.verification_text_mr', value) : null}
            onEnglishChange={editor ? value => edit('footer.verification_text_en', value) : null}
            onMarathiActivate={editor ? () => editor.activate('footer.verification_text_mr') : null}
            onEnglishActivate={editor ? () => editor.activate('footer.verification_text_en') : null}
            multiline
          />
          {issuance?.template_version && (
            <small>Template v{issuance.template_version} · Revision {issuance.revision || context.certificate.revision || 1}</small>
          )}
        </div>}
      </footer> : editor && <div
        className={`gov-cert-hidden-region ${dragOverRegion === 'footer' ? 'gov-cert-drag-over' : ''}`}
        onDragOver={e => handleRegionDragOver(e, 'footer')}
        onDragLeave={e => handleRegionDragLeave(e, 'footer')}
        onDrop={e => handleRegionDrop(e, 'footer.content_blocks')}
      >Footer hidden — enable it from Footer settings</div>}
    </article>
  );
}
